<template>
  <section class="roster-confirmation">
    <div class="card">
      <div class="card__header">
        <h1>{{ isAccess ? 'Company Access' : 'Team Confirmation' }}</h1>
        <p v-if="!isInvalid && !isAccess" class="subtitle">Let {{ counterpartName }} know where you stand.</p>
      </div>

      <div v-if="isLoading" class="state state--loading">
        <div class="spinner" aria-hidden="true"></div>
        <p>Loading invitation details...</p>
      </div>

      <div v-else-if="responded" class="state state--response">
        <h2>Thank you for responding.</h2>
        <p>{{ responseMessage }}</p>
        <b-button type="is-primary" @click="goHome">
          Back to Home
        </b-button>
      </div>

      <!-- spec-admin-gated-company-access: an access request that can't be acted on shows why and no actions. -->
      <div v-else-if="isAccess && accessState" class="state state--response">
        <h2>{{ accessStateTitle }}</h2>
        <p>{{ accessStateMessage }}</p>
        <div class="actions">
          <b-button type="is-text" @click="goHome">Back to Home</b-button>
        </div>
      </div>

      <div v-else-if="isInvalid" class="state state--response">
        <h2>This invite link is invalid or expired</h2>
        <p v-if="managerId && !isApproval && !isMove && !isAccess">
          You can still ask to join this team, and the manager will review your request.
        </p>
        <p v-else>This request is no longer open.</p>
        <div class="actions">
          <b-button
            v-if="managerId && !isApproval && !isMove && !isAccess"
            type="is-primary"
            :loading="actionLoading"
            :disabled="actionLoading"
            @click="requestToJoin"
          >
            Ask to Join
          </b-button>
          <b-button type="is-text" @click="goHome">Back to Home</b-button>
        </div>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <div v-else-if="isAccess" class="card__body">
        <p class="invitation-text">
          <template v-if="accessDetails.source === 'manager_request'">
            <strong>{{ personName(accessDetails.sponsor, 'A manager') }}</strong> asked to add
            <strong>{{ personName(accessDetails.person, 'a new person') }}</strong>
            <span v-if="accessDetails.person && accessDetails.person.email">({{ accessDetails.person.email }})</span>
            to {{ companyName }} and their team.
          </template>
          <template v-else-if="accessDetails.sponsor">
            <strong>{{ personName(accessDetails.person, 'Someone') }}</strong>
            <span v-if="accessDetails.person && accessDetails.person.email">({{ accessDetails.person.email }})</span>
            asked to join {{ companyName }} from <strong>{{ personName(accessDetails.sponsor, 'a manager') }}</strong>'s team invite link.
          </template>
          <template v-else>
            <strong>{{ personName(accessDetails.person, 'Someone') }}</strong>
            <span v-if="accessDetails.person && accessDetails.person.email">({{ accessDetails.person.email }})</span>
            asked to join {{ companyName }}.
          </template>
        </p>
        <p class="hint-text access-meta">
          <span class="access-tag">{{ sourceLabel }}</span>
          <span v-if="accessDetails.person && accessDetails.person.emailVerified" class="access-tag access-tag--ok">Email verified</span>
          <span v-else class="access-tag access-tag--warn">Email not yet verified</span>
          <span v-if="accessDetails.sponsor && accessDetails.sponsorActive === false" class="access-tag access-tag--warn">
            Requesting manager is no longer in the company: approving adds them to the company only
          </span>
        </p>
        <div v-if="accessDetails.canDecide" class="actions">
          <b-button type="is-primary" :loading="actionLoading" :disabled="actionLoading" @click="approveAccess">
            Approve Access
          </b-button>
          <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="declineAccess">
            Decline
          </b-button>
          <p class="hint-text">Approving adds them to the company{{ accessDetails.sponsor ? ' and this team' : '' }}. It never gives admin or financial access.</p>
        </div>
        <template v-else>
          <p class="invitation-text">The tenant owner or a company admin will review it.</p>
          <div v-if="accessDetails.canWithdraw" class="actions">
            <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="withdrawAccess">
              Withdraw Request
            </b-button>
          </div>
        </template>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <div v-else-if="isMove" class="card__body">
        <p class="invitation-text">
          <strong>{{ moveDetails.member.firstName }} {{ moveDetails.member.lastName }}</strong>
          wants to move from
          <strong>{{ personName(moveDetails.currentManager, 'their current manager') }}</strong>'s team to
          <strong>{{ personName(moveDetails.targetManager, 'a new manager') }}</strong>'s team.
        </p>
        <div v-if="moveDetails.canDecide" class="actions">
          <b-button type="is-primary" :loading="actionLoading" :disabled="actionLoading" @click="approveMove">
            Approve Move
          </b-button>
          <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="declineMove">
            Decline Move
          </b-button>
        </div>
        <template v-else>
          <p class="invitation-text">The tenant owner or a company admin will review it.</p>
          <div v-if="moveDetails.canWithdraw" class="actions">
            <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="withdrawMove">
              Withdraw Request
            </b-button>
          </div>
        </template>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <div v-else-if="isApproval" class="card__body">
        <p class="invitation-text">
          <strong>{{ counterpart.firstName }} {{ counterpart.lastName }}</strong>
          ({{ counterpart.email }}) asked to join your team.
        </p>

        <div class="actions">
          <b-button type="is-primary" :loading="actionLoading" :disabled="actionLoading" @click="approveRequest">
            Add to My Team
          </b-button>
          <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="declineRequest">
            Decline Request
          </b-button>
        </div>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <div v-else class="card__body">
        <p class="invitation-text">
          You have been invited to join the team of
          <strong>{{ counterpart.firstName }} {{ counterpart.lastName }}</strong>
          ({{ counterpart.email }}).
        </p>

        <div class="actions">
          <b-button type="is-primary" :loading="actionLoading" :disabled="actionLoading" @click="acceptInvitation">
            Accept Invitation
          </b-button>
          <b-button type="is-text" class="decline" :loading="actionLoading" :disabled="actionLoading" @click="declineInvitation">
            Decline Invitation
          </b-button>
        </div>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
import { savePendingAccessLink } from '@/utils/pendingTeamLink'

// spec-reporting-hierarchy. Three modes:
//  - ?team=<managerId>[&invite=<token>]: you were invited to that manager's team. Accepting needs a
//    valid signed invite token or a pending in-app invite; otherwise you can ask to join, and the
//    manager approves. If you already report to someone else, your current manager is asked to
//    approve the move.
//  - ?requester=<userId>: someone asked to join YOUR team; approve or decline.
//  - ?move=<requestId>: someone on your team (or in your company, for admins) wants to move to
//    another manager's team; approve or decline.
//  - ?access=<requestId> (spec-admin-gated-company-access): someone asked to join the company (a
//    signup, an invite link, or a manager's "Request a new user"); the tenant owner / an admin
//    approves or declines.
// Invites never add anyone to a company directly: a company-less person opening a valid invite
// asks the company's admins for access, sponsored by the manager.
const NEEDS_ADMIN_MESSAGE = 'An admin must add this person to the company first.'

export default {
  name: 'RosterConfirmation',
  data() {
    return {
      counterpart: {
        firstName: '',
        lastName: '',
        email: '',
      },
      accessDetails: {
        person: null,
        sponsor: null,
        company: null,
        source: null,
        sponsorActive: true,
        canDecide: false,
        canWithdraw: false,
      },
      // 'forbidden' | 'closed' | 'superseded' — the request can't be acted on (actions hidden).
      accessState: null,
      moveDetails: {
        member: {},
        currentManager: null,
        targetManager: null,
        canDecide: false,
        canWithdraw: false,
      },
      isLoading: false,
      isInvalid: false,
      actionLoading: false,
      responded: false,
      responseMessage: '',
      errorMessage: '',
      config: {
        headers: { Authorization: this.$cookies.get('teamwrkr-token') },
      },
    };
  },
  created() {
    if (!this.$cookies.isKey("teamwrkr-token")) {
      // An access-request link opened while signed out: sign in first, then land back on it.
      if (this.$route.query.access) {
        savePendingAccessLink(this.$route.query.access);
        this.$router.push('/verify');
        return;
      }
      this.$router.push('/');
      return;
    }
    this.loadDetails();
  },
  watch: {
    // Following a notification link while already on this page only changes the query.
    '$route.query': {
      handler(next, prev) {
        if (JSON.stringify(next) === JSON.stringify(prev)) return;
        this.resetState();
        this.loadDetails();
      },
    },
  },
  computed: {
    ...mapState(useUserStore, ['user', 'notifications', 'loading']),
    // spec-admin-gated-company-access: ?access=<requestId> — the owner / an admin approves or
    // declines someone joining the company (and, for a manager's request, their team).
    accessRequestId() {
      return this.$route.query.access || null
    },
    isAccess() {
      return !!this.accessRequestId
    },
    companyName() {
      return this.accessDetails.company?.name || 'the company'
    },
    sourceLabel() {
      if (this.accessDetails.source === 'manager_request') return 'Requested by a manager'
      if (this.accessDetails.source === 'invite_link') return 'Signed up from a team invite link'
      return 'Signed up with the company email domain'
    },
    accessStateTitle() {
      if (this.accessState === 'forbidden') return "You can't decide this request"
      if (this.accessState === 'superseded') return 'Already in a company'
      return 'This request is no longer open'
    },
    accessStateMessage() {
      if (this.accessState === 'forbidden') return 'Only the tenant owner or a company admin can approve or decline it.'
      if (this.accessState === 'superseded') return 'This person joined a company another way, so the request was closed.'
      return 'Someone may already have approved, declined or withdrawn it.'
    },
    moveRequestId() {
      return this.$route.query.move || null
    },
    isMove() {
      return !!this.moveRequestId
    },
    requesterId() {
      return this.$route.query.requester || null
    },
    isApproval() {
      return !!this.requesterId
    },
    managerId() {
      return this.$route.query.team || this.$route.query.roster || null
    },
    inviteToken() {
      return this.$route.query.invite || null
    },
    counterpartName() {
      if (this.isAccess) return this.personName(this.accessDetails.person, 'this person')
      if (this.isMove) return this.personName(this.moveDetails.member, 'your teammate')
      return this.counterpart.firstName || 'your teammate'
    },
  },
  methods: {
    personName(person, fallback) {
      const name = `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
      return name || fallback
    },
    resetState() {
      this.isInvalid = false;
      this.accessState = null;
      this.responded = false;
      this.responseMessage = '';
      this.errorMessage = '';
    },
    // Maps an access-request API error to a terminal state (actions hidden), or null.
    accessStateFrom(err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (code === 'superseded') return 'superseded';
      if (status === 403) return 'forbidden';
      if (status === 404 || code === 'not_found') return 'closed';
      return null;
    },
    async loadDetails() {
      if (this.isAccess) return this.getAccessDetails();
      if (this.isMove) return this.getMoveDetails();
      return this.getCounterpartDetails();
    },
    async getMoveDetails() {
      this.isLoading = true;
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/roster/move/${encodeURIComponent(this.moveRequestId)}`,
          this.config
        );
        this.moveDetails = response.data;
      } catch (err) {
        console.error('Error fetching move request:', err);
        this.isInvalid = true;
      } finally {
        this.isLoading = false;
      }
    },
    async getAccessDetails() {
      this.isLoading = true;
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/access-requests/${encodeURIComponent(this.accessRequestId)}`,
          this.config
        );
        this.accessDetails = { ...this.accessDetails, ...response.data };
      } catch (err) {
        console.error('Error fetching access request:', err);
        this.accessState = this.accessStateFrom(err) || 'closed';
      } finally {
        this.isLoading = false;
      }
    },
    async postAccess(action) {
      this.actionLoading = true;
      this.errorMessage = '';
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/access-requests/${encodeURIComponent(this.accessRequestId)}/${action}`,
          {},
          this.config
        );
        return { ok: true, data: response.data };
      } catch (err) {
        console.error(`Error calling access-requests ${action}:`, err);
        return { ok: false, error: err };
      } finally {
        this.actionLoading = false;
      }
    },
    confirmDialog(message, confirmText) {
      return new Promise(resolve => {
        this.$buefy.dialog.confirm({
          title: 'Company access',
          message,
          confirmText,
          cancelText: 'Cancel',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    },
    accessFailed(result) {
      const state = this.accessStateFrom(result.error);
      if (state) this.accessState = state;
      else this.errorMessage = this.errorFrom(result);
    },
    async approveAccess() {
      const unverified = this.accessDetails.person && !this.accessDetails.person.emailVerified ? ' Their email is not verified yet.' : '';
      const team = this.accessDetails.sponsor && this.accessDetails.sponsorActive !== false
        ? ` and ${this.personName(this.accessDetails.sponsor, 'their manager')}'s team` : '';
      const ok = await this.confirmDialog(
        `Add ${this.counterpartName} to ${this.companyName}${team}? This does not give admin or financial access.${unverified}`,
        'Approve',
      );
      if (!ok) return;
      const result = await this.postAccess('approve');
      if (result.ok) {
        const joined = result.data?.joinedTeam ? ` and ${this.personName(this.accessDetails.sponsor, 'their manager')}'s team` : '';
        this.finish(`${this.counterpartName} has been added to ${this.companyName}${joined}.`);
      } else {
        this.accessFailed(result);
      }
    },
    async declineAccess() {
      const result = await this.postAccess('decline');
      if (result.ok) this.finish(`We'll let ${this.counterpartName} know their request was declined.`);
      else this.accessFailed(result);
    },
    async withdrawAccess() {
      const result = await this.postAccess('withdraw');
      if (result.ok) this.finish('The access request has been withdrawn.');
      else this.accessFailed(result);
    },
    async getCounterpartDetails() {
      const counterpartId = this.requesterId || this.managerId;
      if (!counterpartId) {
        this.isInvalid = true;
        return;
      }
      this.isLoading = true;
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/roster/${encodeURIComponent(counterpartId)}`,
          { ...this.config, params: this.inviteToken && !this.isApproval ? { invite: this.inviteToken } : {} }
        );
        this.counterpart = response.data;
      } catch (err) {
        // 403 = no valid invite and no open request: say so rather than show blank names.
        console.error('Error fetching team details:', err);
        this.isInvalid = true;
      } finally {
        this.isLoading = false;
      }
    },
    async postRoster(path, body) {
      this.actionLoading = true;
      this.errorMessage = '';
      try {
        const response = await this.axios.post(`${process.env.VUE_APP_API_URL}/users/roster/${path}`, body, this.config);
        return { ok: true, data: response.data };
      } catch (err) {
        console.error(`Error calling roster/${path}:`, err);
        return { ok: false, error: err };
      } finally {
        this.actionLoading = false;
      }
    },
    errorFrom(result) {
      const data = result.error?.response?.data || {};
      if (data.code === 'needs_admin') return NEEDS_ADMIN_MESSAGE;
      if (data.code === 'retry') return 'Someone else changed this at the same time. Please try again.';
      if (data.code === 'self') return "You can't join your own team.";
      if (data.code === 'superseded') return 'This person already joined a company another way, so the request was closed.';
      if (data.code === 'not_found') return 'This request is no longer open.';
      if (data.code === 'other_company') return 'You have already asked to join another company.';
      if (data.code === 'already_requested') return data.message || 'Another manager has already asked for this person.';
      if (data.code === 'no_company') return "This team's company no longer exists.";
      if (data.code === 'admin_only') return 'Only the tenant owner or a company admin can add people to a team.';
      if (data.code === 'needs_admin_team') return 'Ask the tenant owner or a company admin to add you to this team.';
      return data.message || 'Something went wrong. Please try again.';
    },
    finish(message) {
      this.responded = true;
      this.responseMessage = message;
    },
    declinedMessage(result, name) {
      return result.data?.notified ? `We'll let ${name} know you declined.` : 'Your response has been recorded.';
    },
    async acceptInvitation() {
      const result = await this.postRoster('accept', { rosterId: this.managerId, inviteToken: this.inviteToken || undefined });
      if (result.ok) {
        const status = result.data?.status;
        if (status === 'access_requested') {
          this.finish(`Your company admin has been asked to approve your access. You'll join ${this.counterpartName}'s team once they approve.`);
        } else if (status === 'needs_teamwrkr') {
          this.finish('This company has no admin who can approve new people yet. Please contact Teamwrkr.');
        } else if (status === 'move_requested') {
          this.finish(`You already report to someone else. A company admin has been asked to approve your move to ${this.counterpartName}'s team.`);
        } else if (status === 'member') {
          this.finish(`You're already on ${this.counterpartName}'s team.`);
        } else {
          this.finish(`You've been added to ${this.counterpartName}'s team.`);
        }
      } else if (result.error?.response?.data?.code === 'no_invitation') {
        this.isInvalid = true;
      } else {
        this.errorMessage = this.errorFrom(result);
      }
    },
    async declineInvitation() {
      const result = await this.postRoster('decline', { rosterId: this.managerId });
      if (result.ok) this.finish(this.declinedMessage(result, this.counterpartName));
      else this.errorMessage = this.errorFrom(result);
    },
    async requestToJoin() {
      const result = await this.postRoster('request', { rosterId: this.managerId });
      if (result.ok) {
        this.finish(result.data?.status === 'member'
          ? `You're already on this team.`
          : result.data?.access === 'requested'
            ? `We've asked your company admin to approve your access and the manager to add you. You'll be notified once they decide.`
            : `We've asked the manager to add you. You'll be notified once they decide.`);
      } else {
        this.errorMessage = this.errorFrom(result);
      }
    },
    async approveRequest() {
      const result = await this.postRoster('accept', { requesterId: this.requesterId });
      if (result.ok) {
        this.finish(result.data?.status === 'move_requested'
          ? `${this.counterpartName} already reports to another manager. A company admin has been asked to approve the move.`
          : `${this.counterpartName} is now on your team.`);
      } else {
        this.errorMessage = this.errorFrom(result);
      }
    },
    async declineRequest() {
      const result = await this.postRoster('decline', { requesterId: this.requesterId });
      if (result.ok) this.finish(this.declinedMessage(result, this.counterpartName));
      else this.errorMessage = this.errorFrom(result);
    },
    async approveMove() {
      const result = await this.postRoster('accept', { moveRequestId: this.moveRequestId });
      if (result.ok) this.finish(`${this.counterpartName} has moved to ${this.personName(this.moveDetails.targetManager, 'their new manager')}'s team.`);
      else this.errorMessage = this.errorFrom(result);
    },
    async declineMove() {
      const result = await this.postRoster('decline', { moveRequestId: this.moveRequestId });
      if (result.ok) this.finish(this.declinedMessage(result, this.counterpartName));
      else this.errorMessage = this.errorFrom(result);
    },
    async withdrawMove() {
      const result = await this.postRoster(`move/${encodeURIComponent(this.moveRequestId)}/withdraw`, {});
      if (result.ok) this.finish('The move request has been withdrawn.');
      else this.errorMessage = this.errorFrom(result);
    },
    goHome() {
      this.$router.push('/');
    }
  }
};
</script>

<style lang="scss" scoped>
.roster-confirmation {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, $color-surface-cool 0%, $color-surface 100%);

  .card {
    width: 100%;
    max-width: 520px;
    padding: 2.5rem;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(15, 30, 64, 0.12);
    background: $color-surface;
    text-align: center;

    &__header {
      margin-bottom: 1.5rem;

      h1 {
        font-size: 1.8rem;
        margin-bottom: 0.5rem;
      }

      .subtitle {
        color: $color-text-muted;
        margin: 0;
      }
    }

    .invitation-text {
      font-size: 1.05rem;
      color: $color-text-strong;
      margin-bottom: 2rem;

      strong {
        color: $color-text-strong;
      }
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      .decline {
        color: $color-accent;
      }
    }

    .error-text {
      margin-top: 1rem;
      color: $color-danger;
    }

    .hint-text {
      font-size: $font-size-sm;
      color: $color-text-muted;
      margin: 0;
    }

    .access-meta {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }

    .access-tag {
      padding: 0.1rem 0.6rem;
      border-radius: $radius-pill;
      background: $color-surface-cool;
      color: $color-text-muted;
      font-size: $font-size-xs;

      &--ok {
        background: $color-success-soft;
        color: $color-success-strong;
      }

      &--warn {
        background: rgba($color-warning, 0.15);
        color: $color-text-strong;
      }
    }
  }
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  &--loading {
    color: $color-text-muted;
  }

  &--response {
    h2 {
      margin-bottom: 0.5rem;
    }

    p {
      color: $color-text-muted;
    }
  }
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid rgba(33, 150, 243, 0.25);
  border-top-color: $color-info;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .roster-confirmation {
    padding: 1rem;

    .card {
      padding: 1.75rem;
    }
  }
}
</style>
